import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isHome = location.pathname === '/';

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setDrawerOpen(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <>
      <header className="sticky top-0 z-50 w-full bg-primary-950 text-background-50">
        <div className="flex h-16 w-full items-center gap-4 px-4 md:gap-8 md:px-8">
          <Link to="/" className="group flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-primary-950">
              <i className="ri-play-fill text-xl" />
            </span>
            {isHome ? (
              <h1 className="whitespace-nowrap font-heading text-lg font-extrabold tracking-tight">
                麻豆视频
              </h1>
            ) : (
              <span className="whitespace-nowrap font-heading text-lg font-extrabold tracking-tight">
                麻豆视频
              </span>
            )}
            <span className="hidden border-l border-primary-700 pl-2.5 text-[11px] font-normal tracking-widest text-primary-200 lg:inline">
              高清影视
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`cursor-pointer rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive(item.to)
                    ? 'bg-primary-800 font-medium text-accent-300'
                    : 'text-primary-100 hover:bg-primary-900 hover:text-background-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden md:block" role="search">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-primary-300" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片、演员、类型"
                  aria-label="全站搜索"
                  className="h-9 w-52 rounded-md border border-primary-800 bg-primary-900/80 pl-9 pr-3 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-500 focus:outline-none lg:w-72"
                />
              </div>
            </form>

            <Link
              to="/search"
              aria-label="搜索"
              className="flex h-9 w-9 items-center justify-center rounded-md text-primary-100 transition-colors hover:bg-primary-900 hover:text-background-50 md:hidden"
            >
              <i className="ri-search-line text-lg" />
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-primary-100 transition-colors hover:bg-primary-900 hover:text-background-50 md:hidden"
            >
              <i className="ri-menu-line text-xl" />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-[60] md:hidden ${drawerOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!drawerOpen}
      >
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-primary-950/60 transition-opacity duration-300 ${
            drawerOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <aside
          className={`absolute right-0 top-0 flex h-full w-[280px] max-w-[85vw] flex-col bg-primary-950 px-5 py-5 text-background-50 transition-transform duration-300 ${
            drawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between pb-5">
            <span className="font-heading text-base font-bold">麻豆视频</span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setDrawerOpen(false)}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-primary-900 text-primary-100"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <form onSubmit={submitSearch} role="search" className="pb-4">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-primary-300" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视内容"
                aria-label="全站搜索"
                className="h-10 w-full rounded-md border border-primary-800 bg-primary-900 pl-9 pr-3 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-500 focus:outline-none"
              />
            </div>
          </form>

          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                  isActive(item.to)
                    ? 'bg-primary-900 font-medium text-accent-300'
                    : 'text-primary-100'
                }`}
              >
                <span>{item.label}</span>
                <i className="ri-arrow-right-s-line text-base opacity-60" />
              </Link>
            ))}
          </nav>

          <div className="mt-auto border-t border-primary-800 pt-4 text-xs leading-relaxed text-primary-300">
            本站影视条目、评分与演职人员均为演示数据，播放区域仅作界面演示。
          </div>
        </aside>
      </div>
    </>
  );
}